import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { OPERATOR_MAP, OPERATOR_LABELS, VALUE_REQUIRED_OPERATORS } from '../../utils/filters/filterConstants';
import SelectOptionPicker from './inputs/SelectOptionPicker';
import PersonPicker from './inputs/PersonPicker';
// RelativeDatePicker functionality is now integrated into GridDateValuePicker
import RelationPicker from './inputs/RelationPicker';
import GridDateValuePicker from './inputs/GridDateValuePicker';

const ICON_MAP = {
    'text': 'description',
    'number': 'numbers',
    'currency': 'payments',
    'percentage': 'percent',
    'select': 'format_list_bulleted',
    'multi_select': 'sell',
    'status': 'radio_button_checked',
    'date': 'calendar_today',
    'date_range': 'date_range',
    'created_at': 'history',
    'updated_at': 'update',
    'person': 'person',
    'multi_person': 'groups',
    'created_by': 'person_add',
    'checkbox': 'check_box',
    'email': 'mail',
    'phone': 'phone',
    'url': 'link',
    'relation': 'sync_alt',
    'formula': 'functions'
};

export const getFieldIcon = (field) => {
    if (!field) return 'list';
    if (field.icon && React.isValidElement(field.icon) && field.icon.props?.name) {
        return field.icon.props.name;
    }
    const id = String(field.id || '').toLowerCase();
    const label = String(field.label || '').toLowerCase();
    if (id === 'secondvoice' || label.includes('2nd voice') || label.includes('second voice')) return 'person';
    if (id === 'amount' || id === 'budget' || label.includes('amount') || label.includes('budget')) return 'tag';
    if (id === 'segment' || label.includes('vertical') || label.includes('segment')) return 'bolt';
    if (id.includes('type') || label.includes('type')) return 'match_case';
    if (id === 'salesrep' || label.includes('assigned to') || label.includes('sales rep')) return 'assignment_add';
    if (id === 'qaassigned' || label.includes('qa owner')) return 'assignment_add';
    if (id === 'pmname' || label.includes('pm name')) return 'person';
    if (id.includes('company') || label.includes('company')) return 'business';
    if (id.includes('contact') || id.includes('lead') || label.includes('contact') || label.includes('lead')) return 'person';
    return ICON_MAP[field.type] || 'description';
};


/* ── Reusable custom dropdown (replaces native <select>) ── */
export const CustomDropdown = ({ value, options, onChange, icon, minWidth = 'auto', style: extraStyle = {}, className = '' }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { 
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false); 
                setSearch('');
            }
        };
        if (open) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const selectedLabel = options.find(o => o.value === value)?.label || value;
    const filteredOptions = options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));

    return (
        <div ref={ref} className={`relative ${className}`} style={{ ...extraStyle }}>
            <div
                onClick={() => setOpen(!open)}
                className={`relative flex items-center h-7 gap-1.5 pl-3 pr-7 rounded-md border text-[13px] font-normal cursor-pointer select-none whitespace-nowrap transition-all duration-150 ${open ? 'border-[color:var(--border-strong)] bg-[color:var(--bg-subtle)]' : 'border-[var(--border)] bg-[color:var(--bg-subtle)] hover:bg-[var(--bg-muted)] hover:border-[color:var(--border-strong)]'} text-[var(--text-primary)]`}
                style={{ minWidth }}
            >
                {icon && <ObliqueIcon name={icon} size={14} color="var(--text-tertiary)" />}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedLabel}</span>
                <ObliqueIcon name="keyboard_arrow_down" size={14} color="var(--text-tertiary)"
                    style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)' }}
                />
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 4 }}
                        transition={{ duration: 0.12 }}
                        className="absolute top-[calc(100%+4px)] left-0 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-xl shadow-[0_4px_24px_rgba(0,0,0,0.12)] z-[5000] min-w-[200px] max-h-[320px] flex flex-col p-1.5"
                    >
                        {options.length > 5 && (
                            <div className="mb-1">
                                <div className="relative">
                                    <ObliqueIcon name="search" size={14} color="var(--text-tertiary)" className="absolute left-2.5 top-1/2 -translate-y-1/2" />
                                    <input 
                                        autoFocus
                                        type="text" 
                                        placeholder="Search..." 
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="h-7 w-full bg-[color:var(--bg-subtle)] border border-[var(--border)] rounded-md pl-7 pr-2 text-[13px] text-[var(--text-primary)] outline-none focus:border-[color:var(--border-strong)] focus:bg-[var(--bg-surface)]"
                                    />
                                </div>
                            </div>
                        )}
                        <div className="flex-1 overflow-y-auto no-scrollbar">
                            {filteredOptions.length > 0 ? filteredOptions.map(opt => (
                                <div
                                    key={opt.value}
                                    onClick={() => { onChange(opt.value); setOpen(false); setSearch(''); }}
                                    className={`cursor-pointer text-[13px] text-[var(--text-primary)] rounded-[6px] flex items-center px-2.5 py-1.5 gap-2 transition-colors duration-150 ${opt.value === value ? 'bg-[var(--bg-muted)]' : 'hover:bg-[var(--bg-muted)]'}`}
                                >
                                    {opt.icon && (
                                        <div className="flex items-center justify-center shrink-0 w-4 h-4">
                                            <ObliqueIcon name={opt.icon} size={16} color={opt.value === value ? "var(--text-primary)" : "var(--text-secondary)"} />
                                        </div>
                                    )}
                                    <span>{opt.label}</span>
                                </div>
                            )) : (
                                <div className="p-[6px_8px] text-xs text-[var(--text-tertiary)] text-center">No results</div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const FilterRuleRow = ({ rule, schema, index, groupOp, onUpdate, onDelete, onDuplicate, onTurnIntoGroup, onChangeGroupOp }) => {
    const field = schema.find(s => s.id === rule.fieldId);
    const [optionsOpen, setOptionsOpen] = useState(false);
    const optionsRef = useRef(null);
    const rowRef = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (optionsRef.current && !optionsRef.current.contains(e.target)) setOptionsOpen(false);
        };
        if (optionsOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [optionsOpen]);

    const handleFieldChange = (newFieldId) => {
        const newField = schema.find(s => s.id === newFieldId);
        if (!newField) return;
        const validOps = OPERATOR_MAP[newField.type] || ['is'];
        onUpdate({ fieldId: newFieldId, propertyId: newFieldId, fieldType: newField.type, operator: validOps[0], value: undefined });
    };

    const handleOperatorChange = (newOp) => {
        if (!VALUE_REQUIRED_OPERATORS.has(newOp)) onUpdate({ operator: newOp, value: undefined });
        else onUpdate({ operator: newOp });
    };

    const validOperators = OPERATOR_MAP[rule.fieldType] || ['is'];
    const showValueInput = VALUE_REQUIRED_OPERATORS.has(rule.operator);

    // Build dropdown options
    const fieldOptions = schema.filter(s => !s.hidden).map(s => ({
        value: s.id, label: s.label, icon: getFieldIcon(s)
    }));
    const operatorOptions = validOperators.map(op => ({
        value: op, label: OPERATOR_LABELS[op] || op
    }));

    const renderValueInput = () => {
        if (!showValueInput) return null;

        const baseInputClass = "h-7 px-3 border border-[var(--border)] rounded-md text-[13px] w-full bg-[var(--bg-surface)] text-[var(--text-primary)] outline-none shadow-none transition-all duration-150 focus:border-[color:var(--border-strong)]";

        switch (rule.fieldType) {
            case 'text':
            case 'email':
            case 'phone':
            case 'url':
                return <input type="text" placeholder="Value" value={rule.value || ''} onChange={e => onUpdate({ value: e.target.value })} className={baseInputClass} />;
            case 'number':
            case 'currency':
            case 'percentage':
                return <input type="number" placeholder="Value" value={rule.value || ''} onChange={e => onUpdate({ value: parseFloat(e.target.value) })} className={baseInputClass} />;
            case 'select':
            case 'status':
                return <SelectOptionPicker field={field} single value={rule.value} onChange={(val) => onUpdate({ value: val })} />;
            case 'multi_select':
                return <SelectOptionPicker field={field} multi value={rule.value} onChange={(val) => onUpdate({ value: val })} />;
            case 'person':
            case 'created_by':
                return <PersonPicker single value={rule.value} onChange={(val) => onUpdate({ value: val })} />;
            case 'multi_person':
                return <PersonPicker multi value={rule.value} onChange={(val) => onUpdate({ value: val })} />;
            case 'relation':
                return <RelationPicker moduleId={field?.relatedModuleId} value={rule.value} onChange={(val) => onUpdate({ value: val })} />;
            case 'date':
            case 'created_at':
            case 'updated_at':
            case 'date_range':
                return <GridDateValuePicker operator={rule.operator} value={rule.value} onChange={(val) => onUpdate({ value: val })} />;
            default:
                return null;
        }
    };

    return (
        <motion.div
            ref={rowRef}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.15 }}
            className={`relative ${optionsOpen ? 'z-20' : 'z-[1]'} ${index === 0 ? 'ml-0' : 'ml-2'}`}
        >
            <div className="flex items-center gap-2 py-1 box-border">

                {/* Where / And / Or */}
            {index === 0 ? (
                <div className="h-7 flex items-center text-[13px] font-normal text-[var(--text-secondary)] min-w-[52px] pl-2.5 shrink-0 box-border">Where</div>
            ) : index === 1 ? (
                <CustomDropdown
                    value={groupOp}
                    options={[{ value: 'AND', label: 'And' }, { value: 'OR', label: 'Or' }]}
                    onChange={onChangeGroupOp}
                    minWidth="52px"
                    className="shrink-0"
                />
            ) : (
                <div className="h-7 flex items-center text-[13px] font-normal text-[var(--text-secondary)] min-w-[52px] pl-2.5 shrink-0 box-border">
                    {groupOp === 'OR' ? 'Or' : 'And'}
                </div>
            )}

            {/* Field Selector */}
            <div className="shrink-0 max-w-[180px]">
                <CustomDropdown
                    value={rule.fieldId}
                    options={fieldOptions}
                    onChange={handleFieldChange}
                    icon={getFieldIcon(field)}
                />
            </div>

            {/* Operator Selector */}
            <div className="shrink-0 max-w-[140px]">
                <CustomDropdown
                    value={rule.operator}
                    options={operatorOptions}
                    onChange={handleOperatorChange}
                />
            </div>

            {/* Value Selector */}
            {showValueInput && (
                <div className="flex-1 min-w-[120px]">
                    {renderValueInput()}
                </div>
            )}

            {/* Actions */}
            <div ref={optionsRef} className="flex items-center ml-auto pl-2 gap-1 opacity-0 hover:opacity-100 transition-opacity duration-200 group-hover:opacity-100 shrink-0 relative">
                <button
                    onClick={() => setOptionsOpen(!optionsOpen)}
                    className="w-[26px] h-[26px] rounded-md bg-transparent border-none text-[var(--text-tertiary)] flex items-center justify-center cursor-pointer transition-colors duration-150 hover:bg-[var(--bg-muted)] hover:text-[var(--text-primary)]"
                    title="Filter options"
                >
                    <ObliqueIcon name="more_horiz" size={18} />
                </button>
            </div>
            
            <AnimatePresence>
                    {optionsOpen && (
                        <motion.div
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 4 }}
                            transition={{ duration: 0.12 }}
                            className="absolute right-0 top-[calc(100%+4px)] bg-[var(--bg-surface)] border border-[var(--border)] rounded-md shadow-[0_6px_20px_rgba(0,0,0,0.12)] z-[5000] min-w-[160px] p-1"
                        >
                            <div
                                onClick={() => { onDelete(); setOptionsOpen(false); }}
                                className="flex items-center gap-2 px-2.5 py-1.5 text-[13px] text-[#d4555e] cursor-pointer rounded-md transition-colors duration-100 hover:bg-[var(--bg-muted)]"
                            >
                                <ObliqueIcon name="delete" size={15} color="#d4555e" />
                                <span>Delete rule</span>
                            </div>
                            {onDuplicate && (
                                <div
                                onClick={() => { onDuplicate(); setOptionsOpen(false); }}
                                className="flex items-center gap-2 px-2.5 py-1.5 text-[13px] text-[var(--text-primary)] cursor-pointer rounded-md transition-colors duration-100 hover:bg-[var(--bg-muted)]"
                            >
                                <ObliqueIcon name="content_copy" size={15} color="var(--text-tertiary)" />
                                <span>Duplicate</span>
                            </div>
                            )}
                            <div
                                onClick={() => { if (onTurnIntoGroup) onTurnIntoGroup(); setOptionsOpen(false); }}
                                className="flex items-center gap-2 px-2.5 py-1.5 text-[13px] text-[var(--text-primary)] cursor-pointer rounded-md transition-colors duration-100 hover:bg-[var(--bg-muted)]"
                            >
                                <ObliqueIcon name="account_tree" size={15} color="var(--text-tertiary)" />
                                <span>Turn into group</span>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </motion.div>
    );
};

export default FilterRuleRow;
